import { getContractErrorMessage } from "@/lib/contractErrorMessages";
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function money(v: string | number | null | undefined, currency = "EUR"): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("es-ES", { style: "currency", currency });
}

const MILESTONE_STATUS: Record<string, string> = {
  pending: "Pendiente",
  ready_to_invoice: "Listo para facturar",
  accounting_notified: "Contabilidad notificada",
  invoiced: "Facturado",
  sent_to_client: "Enviado al cliente",
  pending_collection: "Pendiente de cobro",
  collected: "Cobrado",
  cancelled: "Cancelado",
};

const MILESTONE_TYPES = [
  ["fixed_amount", "Importe fijo"],
  ["percentage", "Porcentaje"],
  // ["monthly_certification", "Certificación mensual"] — oculto (PR A §4.7):
  // la certificación mensual requerirá revisión hasta PR D; no se ofrece al crear.
] as const;

interface MilestoneRow {
  id: number;
  label: string;
  type: string;
  percentage: string | null;
  fixedAmount: string | null;
  status: string;
  dueDate: string | null;
  notes?: string | null;
}

interface Props {
  contractId: number;
  canWriteEconomic: boolean;
  effectiveAmount?: string | null;
  currency?: string;
}

export function ContractMilestonesSection({ contractId, canWriteEconomic, effectiveAmount, currency = "EUR" }: Props) {
  const utils = trpc.useUtils();
  const milestones = trpc.customers.contracts.economyMilestones.useQuery({ contractId }, { enabled: !!contractId });
  const validation = trpc.customers.contracts.validateMilestonePlan.useQuery({ contractId }, { enabled: canWriteEconomic && !!contractId });
  const createMut = trpc.customers.contracts.createMilestone.useMutation();
  const updateMut = trpc.customers.contracts.updateMilestone.useMutation();
  const cancelMut = trpc.customers.contracts.cancelMilestone.useMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MilestoneRow | null>(null);
  const [label, setLabel] = useState("");
  const [type, setType] = useState<"fixed_amount" | "percentage" | "monthly_certification">("percentage");
  const [percentage, setPercentage] = useState("");
  const [fixedAmount, setFixedAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const rows = (milestones.data ?? []) as MilestoneRow[];

  function invalidate() {
    utils.customers.contracts.economyMilestones.invalidate({ contractId });
    utils.customers.contracts.validateMilestonePlan.invalidate({ contractId });
    utils.customers.contracts.economySummary.invalidate({ contractId });
  }

  function openCreate() {
    setLabel(""); setType("percentage"); setPercentage(""); setFixedAmount(""); setDueDate(""); setNotes("");
    setFormError(null);
    setCreateOpen(true);
  }

  function openEdit(m: MilestoneRow) {
    setLabel(m.label);
    setType((m.type ?? "percentage") as "fixed_amount" | "percentage" | "monthly_certification");
    setPercentage(m.percentage ?? "");
    setFixedAmount(m.fixedAmount ?? "");
    setDueDate(m.dueDate ?? "");
    setNotes(m.notes ?? "");
    setFormError(null);
    setEditTarget(m);
  }

  async function handleCreate() {
    if (!label.trim()) { setFormError("El nombre del hito es obligatorio."); return; }
    setSaving(true); setFormError(null);
    try {
      await createMut.mutateAsync({
        contractId,
        label: label.trim(),
        type,
        percentage: type === "percentage" ? (percentage ? Number(percentage) : null) : null,
        fixedAmount: type === "fixed_amount" ? (fixedAmount || null) : null,
        dueDate: dueDate || null,
        notes: notes || null,
      });
      toast.success("Hito creado");
      setCreateOpen(false);
      invalidate();
    } catch (e) {
      setFormError(getContractErrorMessage(e));
    } finally { setSaving(false); }
  }

  async function handleUpdate() {
    if (!editTarget) return;
    if (!label.trim()) { setFormError("El nombre del hito es obligatorio."); return; }
    setSaving(true); setFormError(null);
    try {
      await updateMut.mutateAsync({
        milestoneId: editTarget.id,
        contractId,
        label: label.trim(),
        percentage: type === "percentage" ? (percentage ? Number(percentage) : null) : null,
        fixedAmount: type === "fixed_amount" ? (fixedAmount || null) : null,
        dueDate: dueDate || null,
        notes: notes || null,
      });
      toast.success("Hito actualizado");
      setEditTarget(null);
      invalidate();
    } catch (e) {
      setFormError(getContractErrorMessage(e));
    } finally { setSaving(false); }
  }

  async function handleCancel(m: MilestoneRow) {
    try {
      await cancelMut.mutateAsync({ milestoneId: m.id, contractId });
      toast.success("Hito cancelado");
      invalidate();
    } catch (e) {
      toast.error(getContractErrorMessage(e));
    }
  }

  if (milestones.isLoading) {
    return <div className="space-y-3"><Skeleton className="h-40 w-full" /></div>;
  }

  const v = validation.data;

  return (
    <div className="space-y-3">
      {canWriteEconomic && v && !v.valid && (
        <div className="flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{v.warning}</span>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">Hitos de facturación</CardTitle>
          {canWriteEconomic && (
            <Button size="sm" variant="outline" onClick={openCreate} className="min-h-[36px]">
              <Plus className="h-4 w-4 mr-1" />Añadir hito
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No hay hitos definidos para este contrato.</p>
          ) : (
            <div className="space-y-2">
              {rows.map((m) => {
                const calculatedAmt = m.percentage && effectiveAmount
                  ? (Number(m.percentage) / 100) * Number(effectiveAmount)
                  : null;
                return (
                  <div key={m.id} className="flex flex-wrap items-start justify-between gap-2 rounded-md border p-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{m.label}</div>
                      <div className="text-xs text-muted-foreground">{m.dueDate ?? "Sin fecha"}</div>
                      {m.notes && <div className="mt-0.5 text-xs text-muted-foreground">{m.notes}</div>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {m.percentage != null ? (
                        <span className="text-sm">{Number(m.percentage).toFixed(0)}%{calculatedAmt != null ? ` · ${money(calculatedAmt, currency)}` : ""}</span>
                      ) : null}
                      {m.type !== "percentage" && m.fixedAmount != null ? <span className="text-sm">{money(m.fixedAmount, currency)}</span> : null}
                      <Badge variant="outline">{MILESTONE_STATUS[m.status] ?? "Estado registrado"}</Badge>
                      {m.type === "monthly_certification" && (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-300">Requiere revisión</Badge>
                      )}
                      {canWriteEconomic && m.status === "pending" && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(m)} title="Editar">
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => handleCancel(m)} title="Cancelar">
                            <X className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo hito</DialogTitle></DialogHeader>
          <MilestoneForm
            label={label} setLabel={setLabel}
            type={type} setType={setType}
            percentage={percentage} setPercentage={setPercentage}
            fixedAmount={fixedAmount} setFixedAmount={setFixedAmount}
            dueDate={dueDate} setDueDate={setDueDate}
            notes={notes} setNotes={setNotes}
            error={formError}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button disabled={saving} onClick={handleCreate}>{saving ? "Guardando..." : "Crear hito"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar hito</DialogTitle></DialogHeader>
          <MilestoneForm
            label={label} setLabel={setLabel}
            type={type} setType={setType}
            percentage={percentage} setPercentage={setPercentage}
            fixedAmount={fixedAmount} setFixedAmount={setFixedAmount}
            dueDate={dueDate} setDueDate={setDueDate}
            notes={notes} setNotes={setNotes}
            error={formError}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancelar</Button>
            <Button disabled={saving} onClick={handleUpdate}>{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function MilestoneForm(props: {
  lockType?: boolean;
  isRetention?: boolean; setIsRetention?: (value: boolean) => void;
  label: string; setLabel: (v: string) => void;
  type: "fixed_amount" | "percentage" | "monthly_certification"; setType: (v: "fixed_amount" | "percentage" | "monthly_certification") => void;
  percentage: string; setPercentage: (v: string) => void;
  fixedAmount: string; setFixedAmount: (v: string) => void;
  dueDate: string; setDueDate: (v: string) => void;
  notes: string; setNotes: (v: string) => void;
  error: string | null;
}) {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="ml-label">Nombre *</Label>
        <Input id="ml-label" value={props.label} onChange={(e) => props.setLabel(e.target.value)} maxLength={255} />
      </div>
      <div className="space-y-1.5">
        <Label>Tipo</Label>
        <Select disabled={props.lockType} value={props.type} onValueChange={(v) => (props.setType(v as typeof props.type), v !== "fixed_amount" && props.setIsRetention?.(false))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {MILESTONE_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {props.type === "percentage" && (
        <div className="space-y-1.5">
          <Label htmlFor="ml-pct">Porcentaje (%)</Label>
          <Input id="ml-pct" type="number" min="0" max="100" step="0.01" value={props.percentage} onChange={(e) => props.setPercentage(e.target.value)} />
        </div>
      )}
      {props.type === "fixed_amount" && (
        <div className="space-y-1.5">
          <Label htmlFor="ml-amt">Importe fijo</Label>
          <Input id="ml-amt" type="number" min="0" step="0.01" value={props.fixedAmount} onChange={(e) => props.setFixedAmount(e.target.value)} />
        </div>
      )}
      {props.type === "fixed_amount" && props.setIsRetention && (
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={props.isRetention ?? false} onChange={e => props.setIsRetention?.(e.target.checked)} />
            Retención pactada
          </label>
          <p className="text-xs text-muted-foreground">Parte del importe contractual, no un importe adicional. No libera retenciones de certificaciones. Aviso interno al consultar el plan desde la fecha prevista.</p>
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="ml-date">Fecha prevista</Label>
        <Input id="ml-date" type="date" value={props.dueDate} onChange={(e) => props.setDueDate(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ml-notes">Notas</Label>
        <Textarea id="ml-notes" value={props.notes} onChange={(e) => props.setNotes(e.target.value)} rows={2} />
      </div>
      {props.error && <p className="text-sm text-destructive">{props.error}</p>}
    </div>
  );
}
